import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";

const FIELDS = [
  { key: "church_name", label: "Church Name" },
  { key: "city", label: "City" },
  { key: "denomination", label: "Denomination" },
  { key: "diocese", label: "Diocese" },
  { key: "tagline", label: "Tagline / Short Welcome Line" },
  { key: "welcome_text", label: "Welcome Message (homepage)", type: "textarea" },
  { key: "address", label: "Church Address" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "map_embed_url", label: "Google Maps Embed URL (optional)" },
  { key: "service_note", label: "Worship Timings Note (contact page)" },
  { key: "hero_image_url", label: "Homepage Hero Image URL" },
];

export default function SettingsAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", form);
      setForm(res.data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="settings-admin" className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy">Site Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Church contact details and homepage content. Replace the placeholder values with real information when ready.
        </p>
      </div>
      <div className="rounded-lg border border-border bg-white p-6 shadow-sm sm:p-8">
        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`setting-${f.key}`}>{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea
                  id={`setting-${f.key}`}
                  data-testid={`setting-${f.key}`}
                  rows={4}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5"
                />
              ) : (
                <Input
                  id={`setting-${f.key}`}
                  data-testid={`setting-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5"
                />
              )}
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} className="bg-navy text-cream hover:bg-navy-light" data-testid="settings-save-btn">
              {saving ? "Saving…" : "Save Settings"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
